import { API_CONFIG } from '../utils/apiConfig';
import { createApiClient } from '../utils/apiClient';
import { QuestionnaireKind } from '../types/QuestionnaireKind';

const DEFAULT_BASE_URL = API_CONFIG.QUESTIONNAIRE_API_URL;

export interface PractitionerServiceConfig {
    baseUrl?: string;
    /**
     * Optional access token override.
     * If not provided, the token will be automatically retrieved from session storage.
     */
    accessToken?: string;
}

type PractitionerPayload = Record<string, unknown>;

/**
 * Gets the API client with automatic token handling.
 * If accessToken is provided in config, it will be used.
 * Otherwise, the token is automatically retrieved from session storage.
 */
function getApiClient(config?: PractitionerServiceConfig) {
    const baseUrl = config?.baseUrl || DEFAULT_BASE_URL;

    if (!baseUrl) {
        throw new Error('Missing API base URL for Practitioner service');
    }

    // If accessToken is explicitly provided, use it; otherwise let the client auto-fetch
    return createApiClient({
        baseUrl,
        accessToken: config?.accessToken,
    });
}

export interface QuestionnaireListParams {
    offset?: number;
    type: QuestionnaireKind;
    /** Only items the current therapist created. Otherwise all shared items plus own private ones. */
    mine?: boolean;
}

export async function listPractitionerQuestionnaires<T = unknown>(
    params: QuestionnaireListParams,
    config?: PractitionerServiceConfig,
): Promise<T> {
    const client = getApiClient(config);

    const searchParams = new URLSearchParams();
    if (typeof params.offset === 'number') {
        searchParams.set('offset', params.offset.toString());
    }
    searchParams.set('type', params.type);
    if (params.mine) {
        searchParams.set('mine', 'true');
    }

    const query = searchParams.toString();
    const path = `/Practitioner/Me/Questionnaire${query ? `?${query}` : ''}`;

    return client.get<T>(path);
}

export interface ParticipationPayload extends PractitionerPayload {
    therapist: string;
    participant: string;
    questionnaire: string;
    startsOn: string;
    endsOn?: string;
}

export async function getPractitionerQuestionnaireById<T = unknown>(
    id: string,
    config?: PractitionerServiceConfig,
): Promise<T> {
    if (!id) {
        throw new Error('Questionnaire id is required');
    }

    const client = getApiClient(config);
    return client.get<T>(`/Questionnaire/${id}`);
}

export async function assignQuestionnaireToPatient<T = unknown>(
    payload: ParticipationPayload,
    config?: PractitionerServiceConfig,
): Promise<T> {
    if (!payload?.therapist || !payload?.participant || !payload?.questionnaire) {
        throw new Error('Participation payload is missing required fields');
    }

    const client = getApiClient(config);
    return client.post<T>('/Participation', payload);
}

export interface TaskListParams {
    offset?: number;
    type: QuestionnaireKind;
}

export async function listPractitionerTasks<T = unknown>(
    params: TaskListParams,
    config?: PractitionerServiceConfig,
): Promise<T> {
    const client = getApiClient(config);

    const searchParams = new URLSearchParams();
    if (typeof params.offset === 'number') {
        searchParams.set('offset', params.offset.toString());
    }
    searchParams.set('type', params.type);

    const query = searchParams.toString();
    const path = `/Practitioner/Me/Task${query ? `?${query}` : ''}`;

    return client.get<T>(path);
}

export async function createNachsorgeTasks<T = unknown>(
    patientId: string,
    config?: PractitionerServiceConfig,
): Promise<T> {
    if (!patientId) {
        throw new Error('Patient id is required');
    }

    const client = getApiClient(config);
    return client.post<T>(`/Nachsorge/${encodeURIComponent(patientId)}`, {});
}

export async function deleteTask<T = unknown>(id: string, config?: PractitionerServiceConfig): Promise<T> {
    if (!id) {
        throw new Error('Task id is required');
    }

    const client = getApiClient(config);
    return client.delete<T>(`/Task/${id}`);
}
